import type { RefObject } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

import { useReducedMotion } from "./useReducedMotion";

interface Options {
  /** 参与 stagger 的元素(相对 scope 的选择器);省略则取容器直接子元素。 */
  selector?: string;
  /** 触发依赖(数据到达后再跑,如 [items.length])。 */
  deps?: unknown[];
  y?: number;
  stagger?: number;
  duration?: number;
}

/**
 * 取参与 stagger 的目标。默认用 el.children(直接子)而非 querySelectorAll(":scope > *")——
 * 后者在 jsdom/nwsapi 上遇到含 Tailwind arbitrary(如 TableBody 的 [&_tr:last-child]:border-0)
 * 的 scope 元素会生成非法 selector 崩溃。
 */
function targetsOf(el: HTMLElement, selector?: string): Element[] {
  return selector ? Array.from(el.querySelectorAll(selector)) : Array.from(el.children);
}

/**
 * 容器内子元素在 mount/数据到达时依次淡入上移(stagger reveal)。
 * 只动 transform/opacity(无 layout thrash);reduced-motion 时跳过,元素保持自然终态。
 * useGSAP 自动 cleanup(unmount 时 revert),scope 限定选择器不外泄。
 */
export function useStaggerReveal(scope: RefObject<HTMLElement>, opts: Options = {}) {
  const reduced = useReducedMotion();
  const { selector, deps = [], y = 12, stagger = 0.05, duration = 0.4 } = opts;
  useGSAP(
    () => {
      if (reduced) return;
      const el = scope.current;
      if (!el) return;
      const targets = targetsOf(el, selector);
      if (!targets.length) return;
      // ★★★ 入场动画的所有权(2026-07-25 G1 门 H-D 实测):**一个元素的入场只能有一个主人**。
      // GSAP 走内联 style,而 CSS 动画层在层叠里压过内联声明 —— 两套并存时 GSAP 必然空转。
      // 实测(三 hub,已上线版本):本 hook 给三张卡写了 8.13 / 8.35 / 8.73px 的内联 Y,
      // computed 却是 0.84 / 1.37 / 2.14 —— 跟着 `scene-cap-in` 走,内联那套一帧没生效。
      // 样片墙更糟:`<Card>` 基类自带 `.fx-enter` 且**没有逐卡延迟**,computed 四张完全相同,
      // = 整墙齐步走,根本没有 stagger。
      // 故:本 hook 接管的容器打上标记,由 index.css 让其直接子元素的 CSS 入场动画让位。
      // (同一条所有权原则,悬浮球的 transform 早已这么钉过一次。)
      // ★挂标记的时机在初始 settle() **之后** —— settle 自己会 release,先挂会被它摘掉。
      // 复位到 CSS 自然态(clearProps 抹掉上一轮残留的 inline opacity/transform)。
      // ★入场跑完立刻把所有权交还 CSS —— 标记不能常驻:它同时挡着 transform 的过渡,
      //   常驻会把卡片 hover 抬升的过渡一起挡掉(那正是这条过渡存在的意义)。
      const release = () => el.removeAttribute("data-stagger-scope");
      const settle = () => {
        gsap.set(targets, { clearProps: "opacity,transform" });
        release();
      };
      // deps 分批异步到位(如权限 vs 军师授权先后 resolve)会让本 hook 重跑,把在途的 stagger tween
      // 打断在中途 —— 每张卡因 stagger 延迟停在不同的 translateY(0/5/9px…),一排卡顶端连成下斜线
      // = 「卡片有点斜」。裸 gsap.from() 被打断后残留 inline transform 永不清除,故:
      //   ① onComplete 正常跑完清残留;② teardown(重跑/卸载)先 kill 再 clearProps 强制回自然位。
      // 两路兜住 → 静止态永远无残留 transform,杜绝阶梯冻结。
      settle();
      el.setAttribute("data-stagger-scope", "");
      const tween = gsap.from(targets, {
        opacity: 0,
        y,
        duration,
        ease: "power2.out",
        stagger,
        overwrite: "auto",
        onComplete: settle,
      });
      return () => {
        tween.kill();
        settle(); // 内含 release():GSAP 不再接管时,所有权立刻交还 CSS
      };
    },
    // revertOnUpdate:true 关键:@gsap/react 默认 deferCleanup(有 deps 且无此项)→ 依赖变化时**只重跑
    // 不 revert**,上面的 teardown 只在 unmount 触发。开 revertOnUpdate 后依赖每次变化都先 kill+settle 再
    // 重跑,teardown 真正兜住「在途 tween 被打断」这条主路径(不再只靠 settle()+overwrite:auto 侧兜)。
    { scope, dependencies: [reduced, ...deps], revertOnUpdate: true }
  );
}
